/** Display formatting. Pure, and deliberately outside the kernel.
 *
 * `packages/core` bans `Intl` and `toLocaleString` (I2) because locale data is
 * ambient and would make a replay hash depend on the machine that ran it. This
 * is *not* core: it formats text for a human and never feeds a command, so Intl
 * is correct here. Do not "fix" this by hand-rolling a grouping loop.
 *
 * The locale is pinned rather than taken from the browser for one reason: the
 * screenshot harness (`qa/shoot.mjs`) compares renders across machines, and an
 * ambient locale would turn `20,000` into `20 000` on half of them. When the
 * game is localised this becomes a parameter, not an ambient read.
 */

import type { TurnState } from "@game/core";

export const UI_LOCALE = "en-US";

const GROUPED = new Intl.NumberFormat(UI_LOCALE, { useGrouping: true, maximumFractionDigits: 0 });
const PLAIN = new Intl.NumberFormat(UI_LOCALE, { useGrouping: false, maximumFractionDigits: 0 });

/** `20000` -> `"20,000"`. The resource bar and every cost use this. */
export const formatNumber = (n: number): string => GROUPED.format(Math.trunc(n));

/** No grouping, for a value that sits inside a tight slot corner. */
export const formatPlain = (n: number): string => PLAIN.format(Math.trunc(n));

/** `+5` / `-3` / `0`, for a delta in an income or modifier line. */
export function formatDelta(n: number): string {
  const v = Math.trunc(n);
  return v > 0 ? `+${formatNumber(v)}` : formatNumber(v);
}

/**
 * A stack's count for the army socket's plaque.
 *
 * **the classic does not abbreviate.** That was said to come from a sweep of "all 49
 * reference screenshots in `scratchpad/refs/`" - there is no such set on this
 * machine and there never was one of that size, so the sweep is not
 * reproducible.
 *
 * The finding is, and it is easier than the sweep. Re-checked 2026-08-30 on
 * every genuine the classic frame in a reference screenshot that prints a number:
 * the resource bars read `2300`, `10000`, `15000`, `17500` and `95`; the
 * adventure army sockets read `21 28 19 20` and `20 7`; the town garrison reads
 * `16 35 23 36 61 3 23`; the battle count plates read `93 11 14 10 2`; and the
 * hero screen reads `Experience 59684`, `Spell Points 100/100` and army counts
 * `16 4 25`. Not one abbreviation anywhere, and a five-digit run is printed in
 * full in two different places. The claim in the note this replaces - "the way
 * The classic does it" - was invented; this one is not.
 *
 * It is still here, and the reason is a measurement rather than a preference.
 * The plaque is inset by the socket's lip at both ends, so on the rack this
 * screen can afford it has `socket - 2 * lip` of room: 32 - 8 = 24 CSS px at
 * 1920x1080 and 21 - 8 = 13 px at 1366x768. A four-digit run at magnification 1
 * measures 21 px (`measureBitmapText`), which clears the wide column and
 * overruns the narrow one by 8 px - across the creature and over the socket's
 * lit lip, which is the exact defect `COUNT_SHELF_PX` was written to stop.
 *
 * So raw digits are blocked by the same thing the 16px sprite is blocked by:
 * seven sockets in a 174-design-pixel column. Change this WITH the rack (see
 * `ARMY_ROW_NOTE` in `InfoPanel.tsx`), not before it - The classic has the room
 * because it prints the count on the wood under a four-column rack, not on a
 * plaque inside a seventh of the panel's width.
 */
export function formatCount(n: number): string {
  const v = Math.trunc(n);
  if (v < 1000) return String(v);
  if (v < 10000) return `${Math.floor(v / 100) / 10}k`;
  if (v < 1000000) return `${Math.floor(v / 1000)}k`;
  return `${Math.floor(v / 100000) / 10}M`;
}

// -------------------------------------------------------------------- calendar

/** The classic's week is seven days and it names them; day 1 of a week is Monday. */
export const DAY_NAMES: readonly string[] = Object.freeze([
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
]);

export const dayName = (day: number): string =>
  DAY_NAMES[((day - 1) % DAY_NAMES.length + DAY_NAMES.length) % DAY_NAMES.length] ?? "";

/** `"Month 2, Week 3, Day 5"` - the line the classic puts under the calendar. */
export const formatDate = (turn: Pick<TurnState, "day" | "week" | "month">): string =>
  `Month ${turn.month}, Week ${turn.week}, Day ${turn.day}`;

/** The compact form for the resource bar, where the panel is 40px tall. */
export const formatDateShort = (turn: Pick<TurnState, "day" | "week" | "month">): string =>
  `M${turn.month} W${turn.week} D${turn.day}`;

// ------------------------------------------------------------------ movement

/**
 * Movement points are stored at 1 point per 1/100th of a base step; the player
 * sees the raw pool, which is what the classic's hero window shows.
 */
export const formatMovement = (points: number): string => formatNumber(points);

// -------------------------------------------------------------------- gauges

/**
 * What a gauge prints on its groove: the value against its cap.
 *
 * A reading with no denominator is not a reading. Movement and mana are both at
 * their ceiling on the turn a game starts, so the sheet shipped showing two
 * full bars captioned `1,600` and `20` - two numbers a player cannot act on,
 * beside an Experience gauge in the same column that did print `0 / 1,000`.
 * The classic prints spell points as `20/20` for exactly this reason.
 *
 * `display` overrides it only where the two ends are not the same quantity, as
 * on the experience gauge, whose BAR measures progress inside the level band
 * while its READING is the absolute total against the next threshold.
 */
export function gaugeReading(value: number, max: number, display?: string | null): string {
  if (display !== undefined && display !== null && display !== "") return display;
  return `${formatNumber(value)} / ${formatNumber(max)}`;
}

// ------------------------------------------------------------- bitmap fitting

/**
 * The largest whole magnification at which `glyphWidth` glyph pixels still fit
 * `room` CSS pixels.
 *
 * A bitmap mark steps by whole magnifications or not at all - that is the whole
 * reason the face exists - so a plate that has to hold a caption and a reading
 * cannot squeeze them, it can only pick a smaller step. Picking the step by
 * MEASURING is what separates this from `useBitmapScale`, which asks the window
 * how wide it is: the same 243px groove holds `MOVEMENT 1,600 / 1,600` at
 * magnification 2 and does not hold `EXPERIENCE 125,000 / 200,000`, and no
 * media query can tell those apart.
 *
 * Never returns 0: a mark that is too big for its plate is drawn at 1 and
 * clipped by the plate, which is visible, rather than not drawn, which is not.
 */
export function fitBitmapScale(room: number, glyphWidth: number, maxScale = 2): number {
  const top = Math.max(1, Math.trunc(maxScale));
  if (room <= 0 || glyphWidth <= 0) return 1;
  for (let s = top; s > 1; s--) {
    if (glyphWidth * s <= room) return s;
  }
  return 1;
}

export const SKILL_LEVEL_NAMES: readonly string[] = Object.freeze(["Basic", "Advanced", "Expert"]);

export const skillLevelName = (level: number): string =>
  SKILL_LEVEL_NAMES[level - 1] ?? String(level);

// -------------------------------------------------------------------- naming

/**
 * A readable name for a content id that has no def behind it yet.
 * `content.mapObjects` is empty as of this commit (STATUS.md), so every object
 * on the map falls through to this. It is a stopgap with a visible seam, not a
 * naming scheme: the moment `MapObjectDef.name` exists, use that instead.
 */
export function humanise(id: string): string {
  // The atlas keys stack category prefixes ("obj.mon_bandit"), so strip until
  // there is nothing left to strip rather than once.
  let stripped = id;
  for (;;) {
    const next = stripped.replace(/^(?:obj|mon|bld|bank)[._]/, "");
    if (next === stripped) break;
    stripped = next;
  }
  const cleaned = stripped.replace(/[._]+/g, " ").trim();
  if (cleaned === "") return id;
  return cleaned.replace(/\b[a-z]/g, (c) => c.toUpperCase());
}
